import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { BOOKING_STATUS, RESOURCE_TYPES, fmtDateTimeTH } from './aoShared'

// ธุรการ: อนุมัติ/ไม่อนุมัติการจองห้อง/รถ
export default function BookingApproval() {
  const [status, setStatus] = useState('pending')
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      const res = await axiosInstance.get(`/api/ao/bookings?${params.toString()}`)
      if (res.data.success) setBookings(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { fetchData() }, [fetchData])

  const handleApprove = async (b) => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'อนุมัติการจอง?',
      text: `${b.rs_name} · ${b.booked_by} · ${fmtDateTimeTH(b.bk_start)} - ${fmtDateTimeTH(b.bk_end)}`,
      showCancelButton: true,
      confirmButtonText: 'อนุมัติ',
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/bookings/${b.bk_id}/approve`, {})
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'อนุมัติไม่สำเร็จ', text: err.response?.data?.message || '' })
      fetchData()
    }
  }

  const handleReject = async (b) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'ไม่อนุมัติการจอง?',
      text: `${b.rs_name} · ${b.booked_by}`,
      input: 'text',
      inputPlaceholder: 'เหตุผล (ไม่บังคับ)',
      showCancelButton: true,
      confirmButtonText: 'ไม่อนุมัติ',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/bookings/${b.bk_id}/reject`, { note: result.value || '' })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ดำเนินการไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-calendar-check-line me-2 text-primary" />
              อนุมัติการจองห้อง / รถ
            </h4>
            <div className="d-flex gap-2">
              <Button as={Link} to="/booking-calendar" variant="outline-primary" size="sm">
                <i className="ri-calendar-line me-1" />ปฏิทินการจอง
              </Button>
              <Button as={Link} to="/admin-office/resources" variant="outline-secondary" size="sm">
                <i className="ri-building-line me-1" />จัดการห้อง/รถ
              </Button>
              <Form.Select size="sm" style={{ width: 140 }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="pending">รออนุมัติ</option>
                <option value="approved">อนุมัติแล้ว</option>
                <option value="rejected">ไม่อนุมัติ</option>
                <option value="cancelled">ยกเลิก</option>
                <option value="">ทั้งหมด</option>
              </Form.Select>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Body className="p-0">
                {bookings.length === 0 ? (
                  <div className="text-center text-muted py-4">ไม่มีรายการในเงื่อนไขที่เลือก</div>
                ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th>ทรัพยากร</th>
                        <th>ผู้จอง</th>
                        <th>เริ่ม</th>
                        <th>สิ้นสุด</th>
                        <th>วัตถุประสงค์</th>
                        <th style={{ width: 120 }}>สถานะ</th>
                        <th style={{ width: 170 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => {
                        const st = BOOKING_STATUS[b.bk_status] || BOOKING_STATUS.pending
                        const rt = RESOURCE_TYPES[b.rs_type] || RESOURCE_TYPES.room
                        return (
                          <tr key={b.bk_id}>
                            <td><Badge bg={rt.bg} className="me-1">{rt.label}</Badge>{b.rs_name}</td>
                            <td>{b.booked_by}</td>
                            <td>{fmtDateTimeTH(b.bk_start)}</td>
                            <td>{fmtDateTimeTH(b.bk_end)}</td>
                            <td className="text-truncate" style={{ maxWidth: 200 }} title={b.bk_purpose}>
                              {b.bk_purpose}
                              {b.bk_destination && <div className="text-muted" style={{ fontSize: '0.75rem' }}>ปลายทาง: {b.bk_destination}</div>}
                            </td>
                            <td>
                              <Badge bg={st.bg}>{st.label}</Badge>
                              {b.approve_note && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{b.approve_note}</div>}
                            </td>
                            <td className="text-end">
                              {b.bk_status === 'pending' && (
                                <>
                                  <Button size="sm" variant="success" className="me-1" onClick={() => handleApprove(b)}>
                                    <i className="ri-check-line" /> อนุมัติ
                                  </Button>
                                  <Button size="sm" variant="outline-danger" onClick={() => handleReject(b)}>
                                    ไม่อนุมัติ
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
